// 第三方库
import { makeObservable, observable, computed, action } from 'mobx';

// 内部模块 - Store
import { serviceStore } from './ServiceStore';
import { serviceFactory } from './factories/ServiceFactory';
import { batchUpdateManager } from './BatchUpdateManager';
import { userInfoUpdateStore } from './UserInfoUpdateStore';

// 内部模块 - 工具
import { logger } from '../utils/logger';

/**
 * 聊天用户选择存储 - 极简版本
 * 只管理用户选择状态，不涉及复杂的会话管理
 */
class ChatUserPickerStore {
  constructor() {
    // 状态 - 只保留核心状态
    this._selectedUserId = null;
    this._isInitialized = false;

    // 获取服务
    this._viewUserSelectionManager = serviceFactory.getViewUserSelectionManager();

    // 注册批量更新回调
    this._setupBatchUpdateCallback();

    makeObservable(this, {
      _selectedUserId: observable,
      _isInitialized: observable,

      // 计算属性
      selectedUserId: computed,
      isInitialized: computed,
      shouldRenderConversation: computed,
      viewData: computed,
      currentUserData: computed,
      setSelectedUserId: action,
      setIsInitialized: action,
      handleViewItemClick: action,
      checkAndInitialize: action,
      restoreAndHandleUserClick: action,
      updateUserUnreadCount: action,
      updateUserLastMessage: action,
      _moveUserToTop: action,
      _handleBatchUpdates: action,
      _updateUserUnreadCountDirect: action,
      _addNewUserToList: action,
    });
  }

  // ==================== 计算属性 ====================
  get selectedUserId() {
    return this._selectedUserId;
  }

  get isInitialized() {
    return this._isInitialized;
  }

  get shouldRenderConversation() {
    const result = this.isInitialized && this.selectedUserId;
    logger.debug('[ChatUserPickerStore] shouldRenderConversation', {
      isInitialized: this.isInitialized,
      selectedUserId: this.selectedUserId,
      result,
    });
    return result;
  }

  get viewData() {
    const originalViewData = serviceStore.viewData;
    if (!originalViewData?.data) {
      return originalViewData;
    }
    // 合并 UserInfoUpdateStore 的增量数据
    const mergedData = originalViewData.data.map(user =>
      userInfoUpdateStore.getUserDisplayInfo(user)
    );

    return {
      ...originalViewData,
      data: mergedData,
    };
  }

  get currentUserData() {
    if (!this.selectedUserId || !this.viewData?.data) return null;
    return this.viewData.data.find(
      user => String(user.chat_user_id) === String(this.selectedUserId)
    );
  }

  // ==================== 动作方法 ====================
  setSelectedUserId = userId => {
    this._selectedUserId = String(userId);
    logger.debug('[ChatUserPickerStore] 用户选择', { userId: this._selectedUserId });
  };

  setIsInitialized = value => {
    this._isInitialized = value;
  };

  checkAndInitialize = async () => {
    const hasViewData = this.viewData !== null && this.viewData !== undefined;
    logger.debug('[ChatUserPickerStore] checkAndInitialize', {
      hasViewData,
      isInitialized: this.isInitialized,
      viewData: this.viewData,
    });

    if (hasViewData && !this.isInitialized) {
      this.setIsInitialized(true);
      logger.debug('[ChatUserPickerStore] 初始化完成');
    } else if (!hasViewData) {
      logger.debug('[ChatUserPickerStore] viewData 尚未加载，等待数据');
    }
  };

  handleViewItemClick = async chatUserId => {
    if (!chatUserId) {
      return;
    }

    const normalizedChatUserId = String(chatUserId);

    if (this.selectedUserId === normalizedChatUserId) {
      return;
    }

    this.setSelectedUserId(normalizedChatUserId);

    // 保存用户选择到本地存储
    this._viewUserSelectionManager.saveViewUserSelection(
      serviceStore.selectedViewId,
      normalizedChatUserId
    );

    logger.debug('[ChatUserPickerStore] 用户选择完成', { chatUserId: normalizedChatUserId });
  };

  restoreAndHandleUserClick = async chatUserId => {
    if (!chatUserId) {
      return;
    }

    await this.handleViewItemClick(chatUserId);
  };

  getDefaultUserForView(viewId, viewData) {
    return this._viewUserSelectionManager.getDefaultUserForView(viewId, viewData);
  }

  /**
   * 设置批量更新回调
   */
  _setupBatchUpdateCallback = () => {
    batchUpdateManager.registerUpdateCallback(this._handleBatchUpdates);
  };

  /**
   * 处理批量更新
   * @param {Array} updates - 批量更新数组
   */
  _handleBatchUpdates = updates => {
    logger.debug('[ChatUserPickerStore] 处理批量更新', {
      updateCount: updates.length,
    });

    // 按类型分组处理更新
    const unreadCountUpdates = [];
    const messageUpdates = [];
    const userMoves = [];

    updates.forEach(update => {
      if ('unreadCount' in update) {
        unreadCountUpdates.push(update);
      }
      if ('message' in update) {
        messageUpdates.push(update);
        userMoves.push(update);
      }
    });

    // 批量处理未读消息更新
    if (unreadCountUpdates.length > 0) {
      this._batchUpdateUnreadCounts(unreadCountUpdates);
    }

    // 批量处理消息更新
    if (messageUpdates.length > 0) {
      this._batchUpdateMessages(messageUpdates);
    }

    // 批量处理用户移动
    if (userMoves.length > 0) {
      this._batchMoveUsersToTop(userMoves);
    }
  };

  /**
   * 批量更新未读消息计数
   * @param {Array} updates - 未读消息更新数组
   */
  _batchUpdateUnreadCounts = updates => {
    if (!this.viewData?.data) return;

    updates.forEach(update => {
      const userIndex = this.viewData.data.findIndex(
        user => String(user.chat_user_id) === String(update.userId)
      );
      if (userIndex !== -1) {
        this.viewData.data[userIndex].unread_count = update.unreadCount;
      }
    });

    logger.debug('[ChatUserPickerStore] 批量更新未读消息计数完成', {
      count: updates.length,
    });
  };

  /**
   * 批量更新消息内容
   * @param {Array} updates - 消息更新数组
   */
  _batchUpdateMessages = updates => {
    if (!this.viewData?.data) return;

    updates.forEach(update => {
      const userIndex = this.viewData.data.findIndex(
        user => String(user.chat_user_id) === String(update.userId)
      );
      if (userIndex !== -1) {
        const message = update.message;
        let messageTime = message.timestamp || message.time || Date.now();

        if (typeof messageTime === 'number' && messageTime < 10000000000) {
          messageTime = messageTime * 1000;
        } else if (typeof messageTime === 'string') {
          messageTime = new Date(messageTime).getTime();
        }

        this.viewData.data[userIndex].last_message = message.content || message.text || '';
        this.viewData.data[userIndex].last_message_time = messageTime;
        this.viewData.data[userIndex].last_message_type = message.type || 'text';
        this.viewData.data[userIndex].last_receive_time = new Date(messageTime).toISOString();
      }
    });

    logger.debug('[ChatUserPickerStore] 批量更新消息内容完成', {
      count: updates.length,
    });
  };

  /**
   * 批量移动用户到顶部
   * @param {Array} updates - 需要移动的用户更新数组
   */
  _batchMoveUsersToTop = updates => {
    if (!this.viewData?.data || updates.length === 0) return;

    // 收集需要移动的用户索引
    const userIndicesToMove = [];
    updates.forEach(update => {
      const userIndex = this.viewData.data.findIndex(
        user => String(user.chat_user_id) === String(update.userId)
      );
      if (userIndex !== -1) {
        userIndicesToMove.push(userIndex);
      }
    });

    if (userIndicesToMove.length === 0) return;

    // 按索引降序排序，避免移动时索引变化
    userIndicesToMove.sort((a, b) => b - a);

    // 提取需要移动的用户
    const usersToMove = [];
    userIndicesToMove.forEach(index => {
      usersToMove.unshift(this.viewData.data[index]);
      this.viewData.data.splice(index, 1);
    });

    // 将用户添加到顶部
    this.viewData.data.unshift(...usersToMove);

    logger.debug('[ChatUserPickerStore] 批量移动用户到顶部完成', {
      movedCount: usersToMove.length,
      userIds: usersToMove.map(u => u.chat_user_id),
    });
  };

  /**
   * 更新指定用户的未读消息计数 - 使用批量更新
   * @param {string} userId - 用户ID
   * @param {number} count - 未读消息计数
   */
  updateUserUnreadCount = (userId, count) => {
    // 添加到批量更新队列
    batchUpdateManager.addUpdate(userId, { unreadCount: count });
  };

  /**
   * 更新指定用户的最新消息 - 使用批量更新
   * @param {string} userId - 用户ID
   * @param {Object} message - 消息对象
   */
  updateUserLastMessage = (userId, message) => {
    if (!userId || !message) {
      logger.warn('[ChatUserPickerStore] updateUserLastMessage 参数不完整', { userId, message });
      return;
    }

    // 添加到批量更新队列
    batchUpdateManager.addUpdate(userId, { message });
  };

  /**
   * 更新指定用户的基本信息 - 头像、昵称等
   * @param {string} userId - 用户ID
   * @param {Object} userInfo - 用户信息对象
   */
  updateUserInfo = action((userId, userInfo) => {
    logger.info('[ChatUserPickerStore] updateUserInfo 被调用', {
      userId,
      userInfo,
      userName: userInfo?.userName,
      chatUserName: userInfo?.chatUserName,
      hasAvatar: !!userInfo?.userAvatar,
    });

    if (!userId || !userInfo) {
      logger.warn('[ChatUserPickerStore] updateUserInfo 参数不完整', { userId, userInfo });
      return;
    }

    if (!this.viewData?.data) {
      logger.warn('[ChatUserPickerStore] viewData.data 不存在', {
        hasViewData: !!this.viewData,
        hasData: !!this.viewData?.data,
        dataLength: this.viewData?.data?.length,
      });
      return;
    }

    const userIndex = this.viewData.data.findIndex(
      user => String(user.chat_user_id) === String(userId)
    );

    logger.debug('[ChatUserPickerStore] 查找用户索引', {
      userId,
      userIndex,
      totalUsers: this.viewData.data.length,
      userList: this.viewData.data.map(u => ({ id: u.chat_user_id, name: u.user_name })),
    });

    if (userIndex !== -1) {
      // 用户存在，合并更新信息
      this._mergeUserInfo(userIndex, userInfo);
    } else {
      // 用户不存在，创建新用户并添加到列表顶部
      logger.info('[ChatUserPickerStore] 用户不存在，创建新用户', {
        userId,
        userInfo,
        totalUsers: this.viewData.data.length,
      });

      this._addNewUserToList(userId, userInfo);
    }
  });

  /**
   * 合并用户信息更新
   * @param {number} userIndex - 用户索引
   * @param {Object} userInfo - 用户信息对象
   */
  _mergeUserInfo = (userIndex, userInfo) => {
    const currentUser = this.viewData.data[userIndex];
    const isPrivateChannel = currentUser.channel === 1;

    // 更新用户名称：私域用户保持序列号，其他用户使用昵称
    if (userInfo.userName || userInfo.nickName) {
      const serialNumber = currentUser.serialNumber;

      if (isPrivateChannel && serialNumber) {
        // 私域用户：保持序列号显示
        logger.debug('[ChatUserPickerStore] 私域用户保持序列号显示', {
          userId: currentUser.chat_user_id,
          serialNumber,
          channel: currentUser.channel,
        });
      } else {
        // 其他渠道用户：使用昵称
        this.viewData.data[userIndex].user_name = userInfo.userName || userInfo.nickName;
      }
    }

    // 更新头像
    if (userInfo.userAvatar || userInfo.avatarUrl) {
      this.viewData.data[userIndex].avatar_url = userInfo.userAvatar || userInfo.avatarUrl;
    }

    // 更新聊天用户信息
    if (userInfo.chatUserName) {
      this.viewData.data[userIndex].chat_user_name = userInfo.chatUserName;
    }
    if (userInfo.chatUserAvatar) {
      this.viewData.data[userIndex].chat_user_avatar = userInfo.chatUserAvatar;
    }

    logger.debug('[ChatUserPickerStore] 用户信息已合并更新', {
      userId: currentUser.chat_user_id,
      userName: userInfo.userName || userInfo.nickName,
      channel: currentUser.channel,
    });
  };

  /**
   * 添加新用户到列表顶部
   * @param {string} userId - 用户ID
   * @param {Object} userInfo - 用户信息对象
   */
  _addNewUserToList = (userId, userInfo) => {
    if (!this.viewData?.data) {
      logger.warn('[ChatUserPickerStore] viewData.data 不存在，无法添加新用户', {
        userId,
        userInfo,
      });
      return;
    }

    // 创建新用户对象
    const newUser = {
      chat_user_id: String(userId),
      user_name: userInfo.userName || userInfo.nickName || `用户${userId}`,
      chat_user_name:
        userInfo.chatUserName || userInfo.userName || userInfo.nickName || `用户${userId}`,
      avatar_url: userInfo.userAvatar || userInfo.avatarUrl || null,
      chat_user_avatar:
        userInfo.chatUserAvatar || userInfo.userAvatar || userInfo.avatarUrl || null,
      last_message: userInfo.lastMessage || '暂无消息',
      last_message_time: userInfo.lastReceiveTime
        ? new Date(userInfo.lastReceiveTime).getTime()
        : Date.now(),
      last_receive_time: userInfo.lastReceiveTime || new Date().toISOString(),
      unread_count: userInfo.unreadCount || 0,
      channel: userInfo.channel || 2, // 默认第三方渠道
      last_message_type: userInfo.messageType || 'text',
      // 添加其他必要字段
      serialNumber: userInfo.serialNumber || null,
    };

    // 添加到列表顶部
    this.viewData.data.unshift(newUser);

    logger.info('[ChatUserPickerStore] 新用户已添加到列表顶部', {
      userId,
      userName: newUser.user_name,
      chatUserName: newUser.chat_user_name,
      hasAvatar: !!newUser.avatar_url,
      totalUsers: this.viewData.data.length,
      newUser,
    });
  };

  /**
   * 将用户移动到列表顶部
   * @param {number} userIndex - 用户在当前列表中的索引
   */
  _moveUserToTop = userIndex => {
    if (!this.viewData?.data || userIndex < 0 || userIndex >= this.viewData.data.length) {
      return;
    }

    const user = this.viewData.data[userIndex];
    if (!user) return;

    // 移除用户
    this.viewData.data.splice(userIndex, 1);
    // 添加到顶部
    this.viewData.data.unshift(user);

    logger.debug('[ChatUserPickerStore] 用户已移动到列表顶部', {
      userId: user.chat_user_id,
      userName: user.user_name,
    });
  };

  /**
   * 直接更新用户未读计数（供BatchUpdateManager调用）
   * @param {string} userId - 用户ID
   * @param {number} unreadCount - 未读消息计数
   */
  _updateUserUnreadCountDirect = (userId, unreadCount) => {
    if (!this.viewData?.data) {
      logger.warn('[ChatUserPickerStore] viewData.data 不存在，无法更新未读计数', {
        userId,
        unreadCount,
      });
      return;
    }

    const userIndex = this.viewData.data.findIndex(
      user => String(user.chat_user_id) === String(userId)
    );

    if (userIndex === -1) {
      logger.warn('[ChatUserPickerStore] 未找到用户，无法更新未读计数', {
        userId,
        unreadCount,
        totalUsers: this.viewData.data.length,
      });
      return;
    }

    const user = this.viewData.data[userIndex];
    if (user) {
      user.unread_count = unreadCount;

      logger.debug('[ChatUserPickerStore] 直接更新用户未读计数', {
        userId,
        unreadCount,
        userName: user.user_name,
      });
    }
  };
}

export const chatUserPickerStore = new ChatUserPickerStore();
